import api from '../../../api/client';
import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table, Spinner, Tabs, Tab } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Line, Pie } from "react-chartjs-2";
import "chart.js/auto";
import config from "../../../config";
import MainCard from "../../../components/Card/MainCard";
import { Statistic, Tag, Progress } from "antd";
import {
  MoneyCollectOutlined,
  LineChartOutlined,
  TransactionOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { formatKES } from "../../../utils/currency";

const ExpensesAnalytics = ({ analytics, maxValues }) => {
  const [loading, setLoading] = useState(false);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-danger">No analytics data available.</p>;
  }

  const { metrics, breakdown, chartData } = analytics;

  // Pie chart for category breakdown
  const categories = breakdown?.byCategory
    ? Object.keys(breakdown?.byCategory)
    : [];
  const categoryTotals = breakdown?.byCategory
    ? Object.values(breakdown?.byCategory).map((c) => c?.total || 0)
    : [];

  const pieData = {
    labels: categories,
    datasets: [
      {
        data: categoryTotals,
        backgroundColor: [
          "#4e73df",
          "#1cc88a",
          "#36b9cc",
          "#f6c23e",
          "#e74a3b",
        ],
      },
    ],
  };

  // Table columns
  const columns = [
    { name: "Category", selector: (row) => row?.category, sortable: true },
    {
      name: "Transactions",
      selector: (row) => row?.transactions,
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) =>
        row?.total?.toLocaleString("en-KE", {
          style: "currency",
          currency: "KES",
        }),
      sortable: true,
    },
  ];

  const tableData = Object?.entries(breakdown?.byCategory).map(
    ([category, val]) => ({
      category,
      transactions: val.transactions,
      total: val.total,
    }),
  );

  // Calculate percentages for progress indicators (if maxValues provided)
  const totalExpensesPercent = maxValues?.totalExpenses
    ? (metrics?.totalExpenses / maxValues.totalExpenses) * 100
    : 0;

  const avgDailyExpensesPercent = maxValues?.avgDailyExpenses
    ? (metrics?.avgDailyExpenses / maxValues.avgDailyExpenses) * 100
    : 0;

  const totalTransactionsPercent = maxValues?.totalTransactions
    ? (metrics?.totalTransactions / maxValues.totalTransactions) * 100
    : 0;

  const column = [
    {
      name: "Category",
      selector: (row) => row.category,
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => new Date(row?.createdAt)?.toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Stakeholder",
      selector: (row) => row.stakeholderId || "N/A",
    },
    {
      name: "Total Cost",
      selector: (row) => `KES ${row?.amount?.toLocaleString()}`,
      sortable: true,
    },
    {
      name: "Paid Amount",
      selector: (row) => `KES ${row?.paidAmount?.toLocaleString()}`,
      sortable: true,
    },
    {
      name: "Balance",
      selector: (row) => `KES ${row?.balance?.toLocaleString()}`,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge ${
            row.status === "Paid"
              ? "bg-success"
              : row.status === "Partially Paid"
              ? "bg-warning text-dark"
              : "bg-danger"
          }`}
        >
          {row?.status}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <>
      {/* Top Metrics */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Total Expenses</Card.Title>
              <h5 className="text-primary">
                KES{" "}
                {metrics?.totalExpenses?.toLocaleString("en-KE", {
                  minimumFractionDigits: 2,
                })}
              </h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Avg Daily Expenses</Card.Title>
              <h5 className="text-success">
                KES{" "}
                {metrics?.avgDailyExpenses?.toLocaleString("en-KE", {
                  minimumFractionDigits: 2,
                })}
              </h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Total Transactions</Card.Title>
              <h5 className="text-warning">{metrics?.totalTransactions}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card title="Top Category" className="shadow-sm text-center" isOption>
            <Card.Body>
              <Card.Title>Top Category</Card.Title>
              <h5 className="text-info">{metrics?.topCategory?.category}</h5>
              <small>
                KES{" "}
                {metrics?.topCategory?.total?.toLocaleString("en-KE", {
                  minimumFractionDigits: 2,
                })}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row>
        <Col md={8} className="mb-4">
          <MainCard title="Expenses Trend" isOption>
            <Line data={chartData} />
          </MainCard>
        </Col>
        <Col md={4} className="mb-4">
          <MainCard title="Category Breakdown" isOption>
            <Pie data={pieData} />
          </MainCard>
        </Col>
      </Row>

      {/* Table */}
      <Tabs defaultActiveKey="wean">
        <Tab eventKey="wean" title="RECENT EXPENSES" className="mt-4">
          <Row>
            <Col>
              <MainCard title="Expenses List" isOption>
                <DataTable
                  columns={column}
                  data={analytics?.transactions}
                  pagination
                  highlightOnHover
                  striped
                  responsive
                  bordered
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 30, 40, 50, 100]}
                  paginationComponentOptions={{
                    rowsPerPageText: "Rows per page:",
                  }}
                  paginationResetDefaultPage={true}
                  paginationTotalRows={analytics?.transactions?.length}
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#279e6c", // Green background
                        color: "#fff", // White text
                        textTransform: "uppercase", // Uppercase text
                        // fontSize: '10px', // Adjust font size for header row
                        minHeight: "1.5rem",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    },
                  }}
                />
              </MainCard>
            </Col>
          </Row>
        </Tab>
        <Tab eventKey="weaners" title="EXPENSES BY CATEGORY" className="mt-4">
          <Row>
            <Col>
              <MainCard title="Expenses by Category" isOption>
                <DataTable
                  columns={columns}
                  data={tableData}
                  pagination
                  highlightOnHover
                  striped
                  bordered
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 30, 40, 50, 100]}
                  paginationComponentOptions={{
                    rowsPerPageText: "Rows per page:",
                  }}
                  paginationResetDefaultPage={true}
                  paginationTotalRows={tableData?.length}
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#279e6c", // Green background
                        color: "#fff", // White text
                        textTransform: "uppercase", // Uppercase text
                        // fontSize: '10px', // Adjust font size for header row
                        minHeight: "1.5rem",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    },
                  }}
                />
              </MainCard>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </>
  );
};

export default ExpensesAnalytics;
